import React, { Component } from "react";
import { withTranslation } from 'react-i18next';
import { TFunction, i18n } from 'i18next';
import { globalContext } from '../helpers/GlobalContexts';
import { DetailsList, IColumn, IDetailsHeaderProps, SelectionMode } from "office-ui-fabric-react/lib/DetailsList";
import { FontSizes } from "@uifabric/styling";
import { GridItem } from './PortalSelector';
import { IIconProps } from "office-ui-fabric-react/lib/Icon";
import { IconButton } from "office-ui-fabric-react/lib/components/Button/IconButton/IconButton";
import { Sticky } from "office-ui-fabric-react/lib/Sticky";
import { IRenderFunction } from "office-ui-fabric-react/lib/Utilities";
import { ScrollablePane } from "office-ui-fabric-react/lib/ScrollablePane";
import DisallowedRecipientCard from "./DisallowedRecipientCard";
import { Link } from "office-ui-fabric-react/lib/components/Link";
import { TextField, ITextField } from "office-ui-fabric-react/lib/TextField";
import { DefaultButton, PrimaryButton } from "office-ui-fabric-react/lib/Button";
import { Stack } from "office-ui-fabric-react/lib/Stack";
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { TooltipHost } from "office-ui-fabric-react/lib/components/Tooltip/TooltipHost";
import { featureDetect } from "../../ts/infrastructure/utils";
/**
 * This interface defines the state of the side panel component.
 */
interface State {
    loading: boolean,
    showEngPanel: boolean,
    showServerAccessError: boolean,
    gridItems: GridItem[],
    selectedItems: Set<string>,
    isCalloutVisible: boolean,
    currentItem: GridItem | null,
    showBulkAllowCallout: boolean,
    bulkJustification: string,
}

/**
 * This interface defines the props of the side panel component.
 */
interface Props {
    t: TFunction,
    tReady: boolean,
    i18n: i18n,
    gridItems: GridItem[],
    canOverride: boolean,
    onGridItemsUpdate: (items: GridItem[]) => void
}

class PortalGridComponent extends Component<Props, State> {
    static contextType = globalContext;
    declare context: React.ContextType<typeof globalContext>;
    private gridColumns: IColumn[];
    private callOutRefs: Map<string, HTMLElement | null> = new Map();
    private bulkAllowButtonRef: HTMLElement | null = null;
    private textAreaRef = React.createRef<ITextField>();

    state = {
        loading: false,
        showEngPanel: false,
        showServerAccessError: false,
        gridItems: this.props.gridItems,
        selectedItems: new Set<string>(),
        isCalloutVisible: false,
        currentItem: null,
        showBulkAllowCallout: false,
        bulkJustification: '',
    };

    constructor(props: Props) {
        super(props);

        this.createContent();
    }

    private focusCalloutTextArea = () => {
        requestAnimationFrame(() => {
            if (this.textAreaRef.current) {
                this.textAreaRef.current.focus();
            }
        });
    };

    private createContent() {
        const { t, canOverride } = this.props;
        const baseColumns = [
            {
                key: 'email',
                name: t('portal.disallowed_header', { count: 1 }),
                minWidth: canOverride ? 150 : 220,
                maxWidth: canOverride ? 200 : 220,
                isResizable: true
            },
            {
                key: 'reason',
                name: t('portal.reason_header'), // will be overridden in _renderCustomHeader.
                minWidth: canOverride ? 180 : 250,
                maxWidth: canOverride ? 640 : 800,
            }
        ];

        this.gridColumns = canOverride 
            ? [
                {
                    key: 'select',
                    name: '',
                    minWidth: 25,
                    maxWidth: 25,
                },
                ...baseColumns,
                {
                    key: 'actions',
                    name: 'Actions',
                    maxWidth: 200,
                    minWidth: 100,
                    isResizable: true,
                    className: 'centered-column',
                }
            ] 
            : baseColumns;
    }

    private handleAllowClick = (item: GridItem) => {
        const updatedItems = this.state.gridItems.map(gridItem => ({
            ...gridItem,
            isCalloutOpen: gridItem.email === item.email
        }));

        this.setState({ 
            gridItems: updatedItems,
            currentItem: item,
            isCalloutVisible: true,
        }, this.focusCalloutTextArea);
    };

    private handleJustificationSubmit = (item: GridItem, justification: string) => {
        const updatedItems = this.state.gridItems.map(gridItem => 
            gridItem.email === item.email 
                ? { ...gridItem, isJustified: true, selected: false, justification } 
                : gridItem
        );

        const apm = this.context.globalService.apm;
        apm?.addLabels({
            "recipients_overriden": updatedItems.filter(item => item.isJustified).map(item => item.email).join(","),
            justification: this.state.bulkJustification,
        });

        this.setState({ 
            gridItems: updatedItems,
            isCalloutVisible: false,
            currentItem: null
        });

        this.props.onGridItemsUpdate(updatedItems);
    };

    private handleCloseJustification = () => {
        const { currentItem } = this.state;

        const updatedItems = this.state.gridItems.map(gridItem => ({
            ...gridItem,
            isCalloutOpen: false,
            justification: gridItem.email === currentItem?.email ? '' : gridItem.justification
        }));
        
        this.setState({
            gridItems: updatedItems,
            isCalloutVisible: false,
            currentItem: null
        });        
    };

    private handleJustificationInputChange = (item: GridItem, newValue: string) => {
        const updatedItems = this.state.gridItems.map(gridItem =>
            gridItem.email === item.email ? { ...gridItem, justification: newValue } : gridItem
        );
        this.setState({ gridItems: updatedItems });
    };

    private handleCheckboxChange = (email: string, checked: boolean) => {
        this.setState(prevState => ({
            gridItems: prevState.gridItems.map(gridItem =>
                gridItem.email === email ? { ...gridItem, selected: checked } : gridItem
            )
        }));
    };

    private handleSelectAllChange = (_ev: React.FormEvent<HTMLElement>, checked?: boolean) => {
        // If checked is explicitly provided, use it
        // If not (or undefined), toggle based on the current state
        const overridableItems = this.state.gridItems.filter(item => item.can_override !== false && !item.isJustified);
        const shouldSelectAll = checked !== undefined ? checked : 
            !(overridableItems.length > 0 && overridableItems.every(item => item.selected));

        const updatedItems = this.state.gridItems.map(item => ({
            ...item,
            selected: (item.isJustified || item.can_override === false) ? false : shouldSelectAll
        }));
        
        this.setState({ gridItems: updatedItems });
    };

    private handleBulkAllowClick = () => {
        this.setState({ showBulkAllowCallout: true }, this.focusCalloutTextArea);
    };

    private handleBulkJustificationSubmit = () => {
        const updatedItems = this.state.gridItems
            .map(item => ({
                ...item,
                isJustified: item.selected ? true : item.isJustified,
                justification: this.state.bulkJustification,
                selected: false
            }));

        const apm = this.context.globalService.apm;
        apm?.addLabels({
            "recipients_overriden": updatedItems.filter(item => item.isJustified).map(item => item.email).join(","),
            justification: this.state.bulkJustification,
        });

        this.setState({ 
            gridItems: updatedItems,
            showBulkAllowCallout: false,
            bulkJustification: '',
        });

        this.props.onGridItemsUpdate(updatedItems);
    };

    private handleCloseBulkJustification = () => {
        this.setState({
            showBulkAllowCallout: false,
            bulkJustification: '',
        });
    };

    /**
     * This callback function is responsible for rendering the grid cells
     */
    protected _renderItemColumn = (item: GridItem, _index: number, column: IColumn) => {
        const { isCalloutVisible, currentItem } = this.state;
        switch (column.key) {
            case 'email':
                return <div style={{ 
                    height: "100%", 
                    display: "flex",
                    opacity: item.isJustified ? 0.5 : 1,
                    flexDirection: "column",
                    justifyContent: "center",
                }}>
                    <div style={{ fontSize: FontSizes.medium, fontWeight: 'bold' }}>
                        {item.name}
                    </div>
                    <div style={{ fontSize: FontSizes.smallPlus, lineHeight: '14px' }}>
                        {item.email}
                    </div>
                </div>;

            case 'reason':
                const AlertIcon: IIconProps = { 
                    iconName: "AlertSolid", 
                    ariaLabel: item.reason, 
                    title: item.reason, 
                    style: { 
                        color: item.isJustified ? "#999" : "#0e285bff", 
                        lineHeight: '13px', 
                        cursor: "initial" 
                    } 
                };
                return <div style={{ 
                    fontSize: FontSizes.medium,
                    color: item.isJustified ? "#999" : "#1F69FF", 
                    display: "flex", 
                    alignItems: "center",
                    opacity: item.isJustified ? 0.5 : 1,
                    textWrap: "wrap"
                }}>
                    <IconButton iconProps={AlertIcon} style={{ paddingTop: '5px' }} />
                            {item.reason_url ? (
                                <Link 
                                    href={item.reason_url}
                                    target="_blank"
                                    style={{ color: item.isJustified ? "#999" : "#1F69FF", textDecoration: "underline" }}
                                >
                                    {item.reason}
                                </Link>
                            ) : (
                                item.reason
                            )}
                </div>;

            case 'select':
                return (
                    <Checkbox
                        checked={item.selected}
                        onChange={(_ev, checked) => this.handleCheckboxChange(item.email, checked)}
                        styles={{ 
                            root: { 
                                paddingTop: '8px',
                                opacity: (item.isJustified || item.can_override === false) ? 0.5 : 1
                            } 
                        }}
                        disabled={item.isJustified || item.can_override === false}
                    />
                );

            case 'actions':
                if (!this.props.canOverride) return null;
                if (item.can_override === false) {
                    return (
                        <TooltipHost content={this.props.t("portal.override_not_allowed_tooltip")}>
                            <PrimaryButton
                                text="Allow"
                                disabled={true}
                                data-recipient={item.email}
                                styles={{
                                    root: {
                                        fontSize: "13px",
                                        padding: "10px",
                                        borderRadius: "6px",
                                    },
                                }}
                            />
                        </TooltipHost>
                    );
                }
                return (
                    <div ref={(el) => this.callOutRefs.set(item.email, el)}>
                        {item.isJustified ? (
                            <PrimaryButton
                                key={`allow-button-${item.email}-${isCalloutVisible}`}
                                text="Allowed"
                                data-recipient={item.email}
                                styles={{
                                    root: {
                                        fontSize: "13px",
                                        padding: "10px",
                                        pointerEvents: "none",
                                        borderRadius: "6px",
                                        color: "#242424",
                                        border: "1px solid #D1D1D1",
                                        backgroundColor: "#fff"
                                    },
                                }}
                            />
                        ) : (
                            <PrimaryButton
                                key={`allow-button-${item.email}-${isCalloutVisible}`}
                                className={`allow-button-${item.email}`}
                                data-recipient={item.email}
                                text="Allow"
                                onClick={() => this.handleAllowClick(item)}
                                styles={{
                                    root: {
                                        fontSize: "13px",
                                        padding: "10px",
                                        borderRadius: "6px",
                                        backgroundColor: "#4181FF"
                                    },
                                }}
                            />
                        )}
                        {isCalloutVisible && currentItem?.email === item.email && (
                            <Callout
                                target={this.callOutRefs.get(item.email)}
                                onDismiss={this.handleCloseJustification}
                                directionalHint={DirectionalHint.rightCenter}
                                isBeakVisible={true}
                                styles={{
                                    root: {
                                        minWidth: 300,
                                        padding: '10px 15px',
                                    },
                                }}
                            >
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <TextField
                                        placeholder="Enter justification..."
                                        multiline
                                        borderless
                                        rows={3}
                                        componentRef={this.textAreaRef}
                                        styles={{
                                            root: { fontSize: "13px" },
                                            field: {
                                                "::placeholder": {
                                                    color: "#707070",
                                                    fontSize: "13px"
                                                }
                                            }
                                        }}
                                        resizable={false} 
                                        onChange={(_, newValue) => {
                                            this.handleJustificationInputChange(item, newValue || '');
                                        }}
                                    />
                                    <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                                        <DefaultButton
                                            text="Cancel"
                                            onClick={this.handleCloseJustification}
                                            styles={{
                                                root: {
                                                    fontSize: "12px",
                                                    padding: "8px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #4181FF",
                                                },
                                            }}
                                        />
                                        <PrimaryButton
                                            text="Submit"
                                            onClick={() => this.handleJustificationSubmit(item, item.justification)}
                                            disabled={!item.justification || item.justification.trim() === ''}
                                            styles={{
                                                root: {
                                                    fontSize: "12px",
                                                    padding: "8px",
                                                    borderRadius: "6px",
                                                    backgroundColor: "#4181FF"
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                            </Callout>
                        )}
                    </div>
                );

            default:
                return 'Unkn';
        }
    }

    private _renderCustomHeader = (props: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps>) => {
        if (!props) {
            return null;
        }
        const { t, i18n, canOverride } = this.props;
        const isHelpLinkEnabled = featureDetect("help_link", t, i18n);

        const customColumns = props.columns.map((column) => {
            if (column.key === 'reason') {
                // @ts-ignore
                column.name = (
                    <span>
                        {t("portal.reason_header")} 
                        {isHelpLinkEnabled && (
                            <>
                                {" "}
                                <Link
                                    style={{ fontSize: "14px", textDecoration: "underline" }}
                                    href={t("help_link.url")}
                                    target="_blank"
                                >
                                    {t("help_link.text")}
                                </Link>
                            </>
                        )}
                    </span>
                );
            }

            if (column.key === 'select' && canOverride) {
                // Only render select-all checkbox if canOverride is true
                // @ts-ignore
                column.name = (
                    <Checkbox
                        checked={
                            this.state.gridItems.filter(item => !item.isJustified && item.can_override !== false).length > 0 &&
                            this.state.gridItems.filter(item => !item.isJustified && item.can_override !== false).every(item => item.selected)
                        }
                        indeterminate={
                            !this.state.gridItems.filter(item => item.can_override !== false).every(item => item.selected || item.isJustified) && 
                            this.state.gridItems.some(item => item.selected)
                        }
                        onChange={this.handleSelectAllChange}
                        styles={{ root: { paddingTop: '8px' } }}
                    />
                );
            }
            return column;
        });

        return (
            <Sticky>
                {defaultRender!({
                    ...props,
                    columns: customColumns,
                })}
            </Sticky>
        );
    };

    public render() {
        return (
            <ScrollablePane
                styles={{ root: { maxWidth: "90%", margin: "0 auto" } }}
            >
                {this.props.canOverride && this.state.gridItems.some(item => item.selected && !item.isJustified) && (
                    <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { padding: '10px 0' } }}>
                        <div ref={(el) => this.bulkAllowButtonRef = el}>
                            <PrimaryButton
                                text={`Allow Selected (${this.state.gridItems.filter(item => item.selected).length})`}
                                onClick={this.handleBulkAllowClick}
                                styles={{
                                    root: {
                                        fontSize: "13px",
                                        padding: "10px",
                                        borderRadius: "6px",
                                        backgroundColor: "#4181FF"
                                    },
                                }}
                            />
                            {this.state.showBulkAllowCallout && (
                                <Callout
                                    target={this.bulkAllowButtonRef}
                                    onDismiss={this.handleCloseBulkJustification}
                                    directionalHint={DirectionalHint.bottomCenter}
                                    isBeakVisible={true}
                                    styles={{
                                        root: {
                                            minWidth: 300,
                                            padding: '10px 15px',
                                        },
                                    }}
                                >
                                    <Stack tokens={{ childrenGap: 8 }}>
                                        <TextField
                                            placeholder="Enter justification for selected recipients..."
                                            multiline
                                            rows={3}
                                            borderless
                                            componentRef={this.textAreaRef}
                                            styles={{
                                                root: { fontSize: "13px" },
                                                field: {
                                                    "::placeholder": {
                                                        color: "#707070",
                                                        fontSize: "13px"
                                                    }
                                                }
                                            }}
                                            resizable={false}
                                            value={this.state.bulkJustification}
                                            onChange={(_, newValue) => {
                                                this.setState({ bulkJustification: newValue || '' });
                                            }}
                                        />
                                        <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                                            <DefaultButton
                                                text="Cancel"
                                                onClick={this.handleCloseBulkJustification}
                                                styles={{
                                                    root: {
                                                        fontSize: "12px",
                                                        padding: "8px",
                                                        borderRadius: "6px",
                                                        border: "1px solid #4181FF",
                                                    },
                                                }}
                                            />
                                            <PrimaryButton
                                                text="Submit"
                                                onClick={this.handleBulkJustificationSubmit}
                                                disabled={!this.state.bulkJustification || this.state.bulkJustification.trim() === ''}
                                                styles={{
                                                    root: {
                                                        fontSize: "12px",
                                                        padding: "8px",
                                                        borderRadius: "6px",
                                                        backgroundColor: "#4181FF"
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Callout>
                            )}
                        </div>
                    </Stack>
                )}
                <DetailsList
                    items={this.state.gridItems}
                    setKey="set"
                    selectionMode={SelectionMode.none}
                    columns={this.gridColumns}
                    onRenderItemColumn={this._renderItemColumn}
                    onRenderDetailsHeader={this._renderCustomHeader}
                />
                <div className="recipient-card" >
                    <DisallowedRecipientCard
                        gridItems={this.state.gridItems}
                        onJustify={this.handleJustificationSubmit}
                        canOverride={this.props.canOverride}
                    />
                </div>
            </ScrollablePane>
        );
    }
}

export const PortalGrid = withTranslation()(PortalGridComponent);